import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { mappingApi, farmerApi } from '../../services/api';

// Add keyframes for spinner animation
const spinKeyframes = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
`;

// Add the keyframes to the document
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = spinKeyframes;
document.head.appendChild(styleSheet);

// Add styles for photo gallery
const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  header: {
    marginBottom: '30px'
  },
  title: {
    fontSize: '24px',
    marginBottom: '20px'
  },
  filters: {
    display: 'flex',
    gap: '20px',
    marginBottom: '20px'
  },
  searchInput: {
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    flex: 1
  },
  locationSelect: {
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #ddd',
    minWidth: '200px'
  },
  statsContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  },
  statCard: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  statTitle: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '10px'
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold'
  },
  farmersList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '20px'
  },
  farmerCard: {
    backgroundColor: '#fff',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
    },
  },
  farmerInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '15px',
  },
  farmerName: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: 'var(--primary-color)',
    marginBottom: '5px',
    borderBottom: '2px solid var(--primary-color)',
    paddingBottom: '10px',
  },
  farmerDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px',
    fontSize: '14px',
    color: '#444',
  },
  detailItem: {
    backgroundColor: '#f8f9fa',
    padding: '12px 15px',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    border: '1px solid #e9ecef',
  },
  detailLabel: {
    fontSize: '12px',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '600',
  },
  detailValue: {
    fontSize: '15px',
    color: '#333',
    fontWeight: '500',
  },
  emissionsInfo: {
    backgroundColor: '#f8f9fa',
    padding: '20px',
    borderRadius: '10px',
    border: '1px solid #e9ecef',
  },
  emissionsTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: 'var(--primary-color)',
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  emissionsDetails: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
  },
  emissionItem: {
    backgroundColor: 'white',
    padding: '12px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  emissionLabel: {
    fontSize: '13px',
    color: '#666',
    marginBottom: '5px',
  },
  emissionValue: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--primary-color)',
  },
  farmerStatus: {
    alignSelf: 'flex-end',
    marginTop: '10px',
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
    gridColumn: '1 / -1'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '18px'
  },
  error: {
    textAlign: 'center',
    padding: '40px',
    color: 'var(--danger-color)',
    fontSize: '18px'
  },
  photoModal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalPhoto: {
    maxWidth: '90%',
    maxHeight: '90vh',
    objectFit: 'contain',
    borderRadius: '8px',
  },
  closeButton: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    color: 'white',
    fontSize: '30px',
    cursor: 'pointer',
    zIndex: 1001,
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: '50%',
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.7)',
    },
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '30px',
    maxWidth: '800px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    position: 'relative',
  },
  closeModalButton: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
    '&:hover': {
      color: '#333',
    },
  },
  modalHeader: {
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '2px solid var(--primary-color)',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: 'var(--primary-color)',
    marginBottom: '10px',
  },
  modalSection: {
    marginBottom: '25px',
  },
  modalSectionTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '15px',
  },
  modalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px',
  },
};

const MappedFarmerPage = () => {
  const { user } = useAuth();
  const { getMappedFarmersForCompany } = useData();
  const [mappedFarmers, setMappedFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [emissionsData, setEmissionsData] = useState({});
  const [totalEmissions, setTotalEmissions] = useState({
    fertilizer: 0,
    pesticide: 0,
    energy: 0,
    irrigation: 0,
    total: 0
  });
  const [loadingEmissions, setLoadingEmissions] = useState(false);
  const [selectedFarmer, setSelectedFarmer] = useState(null);

  useEffect(() => {
    const fetchMappedFarmers = async () => {
      try {
        setLoading(true);
        setError(null);
        setTotalEmissions({
          fertilizer: 0,
          pesticide: 0,
          energy: 0,
          irrigation: 0,
          total: 0
        });
        
        const companyId = user?.companyId;
        if (!companyId) {
          throw new Error('Company ID not found. Please try logging out and logging in again.');
        }

        const response = await mappingApi.getCompanyMappings(companyId);
        let mappingsData = [];
        if (Array.isArray(response)) {
          mappingsData = response;
        } else if (response && response.results && Array.isArray(response.results)) {
          mappingsData = response.results;
        } else if (response && typeof response === 'object') {
          for (const key in response) {
            if (Array.isArray(response[key])) {
              mappingsData = response[key];
              break;
            }
          }
        }

        if (mappingsData.length === 0) {
          setMappedFarmers([]);
          return;
        }

        // Get farmer details for each mapping
        const farmersWithDetails = await Promise.all(
          mappingsData.map(async (mapping) => {
            try {
              const farmerId = typeof mapping.farmer === 'object' ? mapping.farmer.id : mapping.farmer;
              const farmerResponse = await farmerApi.getFarmer(farmerId);
              console.log('Raw farmer response:', farmerResponse); // Log the complete raw response
              
              // Fetch emissions data for this farmer
              let emissionsData = null;
              try {
                const emissionsResponse = await farmerApi.getFarmerEmissions(farmerId);
                console.log('Emissions response for farmer', farmerId, ':', emissionsResponse);
                
                emissionsData = {
                  fertilizer: Number(emissionsResponse.fertilizer_co2_emissions || 0),
                  pesticide: Number(emissionsResponse.pesticide_co2_emissions || 0),
                  energy: Number(emissionsResponse.energy_co2_emissions || 0),
                  irrigation: Number(emissionsResponse.irrigation_co2_emissions || 0),
                  total: Number(emissionsResponse.total_co2_emissions || 0),
                  calculation_date: emissionsResponse.calculation_date,
                  emissions_unit: emissionsResponse.emissions_unit,
                  land_area: emissionsResponse.land_area,
                  land_area_unit: emissionsResponse.land_area_unit
                };
              } catch (error) {
                console.error(`Error fetching emissions for farmer ${farmerId}:`, error);
                emissionsData = {
                  fertilizer: 0,
                  pesticide: 0,
                  energy: 0,
                  irrigation: 0,
                  total: 0,
                  calculation_date: null,
                  emissions_unit: 'kg CO2e',
                  land_area: 0,
                  land_area_unit: 'acres'
                };
              }

              // Update total emissions
              if (emissionsData) {
                setTotalEmissions(prev => ({
                  fertilizer: prev.fertilizer + emissionsData.fertilizer,
                  pesticide: prev.pesticide + emissionsData.pesticide,
                  energy: prev.energy + emissionsData.energy,
                  irrigation: prev.irrigation + emissionsData.irrigation,
                  total: prev.total + emissionsData.total
                }));
              }

              return {
                ...farmerResponse,
                mapping_id: mapping.id,
                status: mapping.status || 'active',
                mappedDate: mapping.created_at || mapping.mappedDate || new Date().toISOString(),
                emissions: emissionsData
              };
            } catch (error) {
              console.error(`Error fetching farmer details for ID ${mapping.farmer}:`, error);
              return {
                ...mapping,
                name: 'Unknown Farmer',
                status: mapping.status || 'active',
                mappedDate: mapping.created_at || mapping.mappedDate || new Date().toISOString(),
                emissions: {
                  fertilizer: 0,
                  pesticide: 0,
                  energy: 0,
                  irrigation: 0,
                  total: 0,
                  calculation_date: null,
                  emissions_unit: 'kg CO2e',
                  land_area: 0,
                  land_area_unit: 'acres'
                }
              };
            }
          })
        );

        console.log('Complete farmers data with photos:', farmersWithDetails);
        setMappedFarmers(farmersWithDetails);
      } catch (error) {
        console.error('Error fetching mapped farmers:', error);
        setError('Failed to load mapped farmers. Please try again.');
      } finally {
        setLoading(false);
        setLoadingEmissions(false);
      }
    };

    if (user) {
      fetchMappedFarmers();
    }
  }, [user]);

  // Filter farmers based on search term and location
  const filteredFarmers = mappedFarmers.filter(farmer => {
    const matchesSearch = searchTerm === '' || 
      (farmer.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (farmer.village || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (farmer.district || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesLocation = locationFilter === '' || 
      (farmer.village || '').toLowerCase() === locationFilter.toLowerCase() ||
      (farmer.district || '').toLowerCase() === locationFilter.toLowerCase();

    return matchesSearch && matchesLocation;
  });

  const handleFarmerClick = (farmer) => {
    setSelectedFarmer(farmer);
  };

  const handleCloseModal = () => {
    setSelectedFarmer(null);
  };

  const renderFarmerModal = () => {
    if (!selectedFarmer) return null;

    return (
      <div style={styles.modal} onClick={handleCloseModal}>
        <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
          <span style={styles.closeModalButton} onClick={handleCloseModal}>&times;</span>
          
          <div style={styles.modalHeader}>
            <h2 style={styles.modalTitle}>{selectedFarmer.name || selectedFarmer.farmer_name}</h2>
          </div>

          <div style={styles.modalSection}>
            <h3 style={styles.modalSectionTitle}>Farmer Details</h3>
            <div style={styles.modalGrid}>
              {Object.entries(selectedFarmer).map(([key, value]) => {
                if (key === 'emissions' || key.includes('emissions') || key.includes('co2')) {
                  return null;
                }

                if ([
                  'id', 
                  'name', 
                  'farmer_name', 
                  'photos',
                  'photo',
                  'image',
                  'images',
                  'farmer_photos',
                  'farmer_photo',
                  'photo_url',
                  'image_url',
                  'profile_photo',
                  'profile_image',
                  'soil_photo',
                  'soil_image',
                  'soil_characteristics_photo',
                  'soil_characteristics_image',
                  'created_at',
                  'updated_at',
                  'sync_status',
                  'mapping_id',
                  'mappedDate',
                  'status'
                ].includes(key) || 
                key.toLowerCase().includes('photo') || 
                key.toLowerCase().includes('image') || 
                key.toLowerCase().includes('url') ||
                (key.toLowerCase().includes('soil') && (
                  key.toLowerCase().includes('photo') || 
                  key.toLowerCase().includes('image') || 
                  key.toLowerCase().includes('url')
                ))) {
                  return null;
                }
                
                if (!value || typeof value === 'function' || typeof value === 'object') {
                  return null;
                }

                if (typeof value === 'string' && (
                  value.startsWith('http') || 
                  value.startsWith('/media') || 
                  value.startsWith('/static') || 
                  value.includes('.jpg') || 
                  value.includes('.jpeg') || 
                  value.includes('.png') || 
                  value.includes('.gif') ||
                  value.includes('soil') && (
                    value.includes('.jpg') || 
                    value.includes('.jpeg') || 
                    value.includes('.png') || 
                    value.includes('.gif')
                  )
                )) {
                  return null;
                }

                return (
                  <div key={key} style={styles.detailItem}>
                    <span style={styles.detailLabel}>{key.replace(/_/g, ' ')}</span>
                    <span style={styles.detailValue}>{value}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={styles.modalSection}>
            <h3 style={styles.modalSectionTitle}>Emissions Data</h3>
            <div style={styles.emissionsDetails}>
              <div style={styles.emissionItem}>
                <div style={styles.emissionLabel}>Fertilizer</div>
                <div style={styles.emissionValue}>
                  {Number(selectedFarmer.emissions?.fertilizer || 0).toFixed(2)} {selectedFarmer.emissions?.emissions_unit || 'kg CO2e'}
                </div>
              </div>
              <div style={styles.emissionItem}>
                <div style={styles.emissionLabel}>Pesticide</div>
                <div style={styles.emissionValue}>
                  {Number(selectedFarmer.emissions?.pesticide || 0).toFixed(2)} {selectedFarmer.emissions?.emissions_unit || 'kg CO2e'}
                </div>
              </div>
              <div style={styles.emissionItem}>
                <div style={styles.emissionLabel}>Energy</div>
                <div style={styles.emissionValue}>
                  {Number(selectedFarmer.emissions?.energy || 0).toFixed(2)} {selectedFarmer.emissions?.emissions_unit || 'kg CO2e'}
                </div>
              </div>
              <div style={styles.emissionItem}>
                <div style={styles.emissionLabel}>Irrigation</div>
                <div style={styles.emissionValue}>
                  {Number(selectedFarmer.emissions?.irrigation || 0).toFixed(2)} {selectedFarmer.emissions?.emissions_unit || 'kg CO2e'}
                </div>
              </div>
              <div style={styles.emissionItem}>
                <div style={styles.emissionLabel}>Total</div>
                <div style={styles.emissionValue}>
                  {Number(selectedFarmer.emissions?.total || 0).toFixed(2)} {selectedFarmer.emissions?.emissions_unit || 'kg CO2e'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <div style={styles.loading}>Loading mapped farmers...</div>;
  }

  if (error) {
    return <div style={styles.error}>{error}</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Mapped Farmers</h1>
        <div style={styles.filters}>
          <input
            type="text"
            placeholder="Search farmers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            style={styles.locationSelect}
          >
            <option value="">All Locations</option>
            {[...new Set(mappedFarmers.map(f => f.village || f.district).filter(Boolean))].map(location => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={styles.statsContainer}>
        <div style={styles.statCard}>
          <h3 style={styles.statTitle}>Total Farmers</h3>
          <p style={styles.statValue}>{mappedFarmers.length}</p>
        </div>
        <div style={styles.statCard}>
          <h3 style={styles.statTitle}>Active Farmers</h3>
          <p style={styles.statValue}>
            {mappedFarmers.filter(f => f.status === 'active').length}
          </p>
        </div>
      </div>

      <div style={styles.farmersList}>
        {filteredFarmers.length === 0 ? (
          <div style={styles.emptyState}>
            <p>No farmers found matching your search criteria.</p>
          </div>
        ) : (
          filteredFarmers.map((farmer) => (
            <div 
              key={farmer.id} 
              style={styles.farmerCard}
              onClick={() => handleFarmerClick(farmer)}
            >
              <h3 style={styles.farmerName}>{farmer.name || farmer.farmer_name}</h3>
              <div style={styles.farmerDetails}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Village</span>
                  <span style={styles.detailValue}>{farmer.village || 'N/A'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>District</span>
                  <span style={styles.detailValue}>{farmer.district || 'N/A'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Total Emissions</span>
                  <span style={styles.detailValue}>
                    {Number(farmer.emissions?.total || 0).toFixed(2)} {farmer.emissions?.emissions_unit || 'kg CO2e'}
                  </span>
                </div>
              </div>
              <div style={styles.farmerStatus}>
                <span style={{
                  ...styles.statusBadge,
                  backgroundColor: farmer.status === 'active' ? 'var(--success-color)' : 'var(--danger-color)'
                }}>
                  {farmer.status || 'active'}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {renderFarmerModal()}
    </div>
  );
};

export default MappedFarmerPage;
